import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar as CalendarIcon, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { formatIsoDate, parseIsoDate, toIsoDate, type ISODate } from '../lib/format'

interface Props {
  value: ISODate | null
  onChange: (value: ISODate | null) => void
  disabled?: boolean
  clearable?: boolean
  placeholder?: string
  className?: string
  size?: 'sm' | 'md'
  invalid?: boolean
  /** Forwarded to the trigger button so an external `<label htmlFor>` (e.g. `FieldRow`) can bind to it. */
  id?: string
  /** Opens the popover as soon as this instance mounts (Task 14: an inline cell editor opens immediately). */
  defaultOpen?: boolean
  /** Notified whenever the popover opens/closes — e.g. so an inline cell editor can cancel on an empty close. */
  onOpenChange?: (open: boolean) => void
  /**
   * Task 14 fix round 1: an inline cell editor needs this stopped from bubbling further — the
   * calendar's day/nav buttons live in a Radix `Portal` (physically outside this component's DOM
   * subtree), but React still dispatches their synthetic events along the *React* tree, so without
   * this a keydown on the popover reaches the gantt grid's own keyboard handler above us (Enter
   * re-opens a different cell's editor, arrow keys move the focused row). Applied to both the
   * trigger wrapper and the portaled `PopoverContent` since either can receive the keydown.
   */
  onKeyDown?: (e: React.KeyboardEvent) => void
}

const IsoDatePicker: React.FC<Props> = ({ value, onChange, disabled, clearable, placeholder, className, size = 'md', invalid, defaultOpen, onOpenChange, onKeyDown, id }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(!!defaultOpen)
  return (
    <div className={cn('flex items-center gap-1', className)} onKeyDown={onKeyDown}>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); onOpenChange?.(o) }}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn('w-full justify-start text-left font-normal bg-card', size === 'sm' ? 'h-8 text-sm' : 'h-10', !value && 'text-muted-foreground', invalid && 'border-destructive')}
          >
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            {value ? formatIsoDate(t, value) : <span>{placeholder || t('datepicker_pick_date')}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" onKeyDown={onKeyDown}>
          <Calendar
            mode="single"
            selected={value ? parseIsoDate(value) : undefined}
            defaultMonth={value ? parseIsoDate(value) : undefined}
            onSelect={(d) => { onChange(d ? toIsoDate(d) : null); setOpen(false) }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {clearable && value && !disabled && (
        <Button type="button" variant="ghost" size="icon" className={size === 'sm' ? 'h-8 w-8' : 'h-10 w-10'} onClick={() => onChange(null)} aria-label={t('Common_clear')}>
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
export default IsoDatePicker
